import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import '../styles/components/MarkdownRenderer.css';

// =============================================================================
// 少数派风格格式化函数（内联以防止 tree-shaking）
// =============================================================================

/**
 * 格式化文本，应用少数派风格规范
 * 内联此函数以确保在构建中被保留
 */
const formatSspaiStyle = (text) => {
  if (!text) return '';

  let result = text;

  // 1. 保护代码块
  const codeBlocks = [];
  result = result.replace(/```[\s\S]*?```/g, (match) => {
    codeBlocks.push(match);
    return `__CODE_BLOCK_${codeBlocks.length - 1}__`;
  });

  // 2. 保护行内代码
  const inlineCodes = [];
  result = result.replace(/`[^`]+`/g, (match) => {
    inlineCodes.push(match);
    return `__INLINE_CODE_${inlineCodes.length - 1}__`;
  });

  // 3. 保护链接
  const links = [];
  result = result.replace(/https?:\/\/[^\s\)]+/g, (match) => {
    links.push(match);
    return `__LINK_${links.length - 1}__`;
  });

  // 4. 中英文字符间添加空格
  result = result.replace(/([\u4e00-\u9fa5])([a-zA-Z0-9])/g, '$1 $2');
  result = result.replace(/([a-zA-Z0-9])([\u4e00-\u9fa5])/g, '$1 $2');
  result = result.replace(/([，。、；：？！])\s+([a-zA-Z0-9])/g, '$1$2');

  // 5. 标点符号规范化（直角引号）
  result = result.replace(/"([^"]*)"/g, '「$1」');
  result = result.replace(/'([^']*)'/g, '『$1』');

  // 6. 省略号规范化
  result = result.replace(/\.\.\./g, '…');
  result = result.replace(/\.\.\.\.\.\./g, '……');
  result = result.replace(/…/g, '……');

  // 7. 恢复链接
  links.forEach((link, i) => {
    result = result.replace(`__LINK_${i}__`, link);
  });

  // 8. 恢复行内代码
  inlineCodes.forEach((code, i) => {
    result = result.replace(`__INLINE_CODE_${i}__`, code);
  });

  // 9. 恢复代码块
  codeBlocks.forEach((block, i) => {
    result = result.replace(`__CODE_BLOCK_${i}__`, block);
  });

  return result;
};

// 简单的回退渲染器，避免 react-markdown 解析错误
const SimpleMarkdownFallback = ({ content }) => {
  if (!content) return null;

  try {
    // 处理表格的函数
    const processTable = (tableText) => {
      const lines = tableText.trim().split('\n');
      const dataLines = lines.filter(line => line.trim().startsWith('|') && line.trim().endsWith('|'));

      if (dataLines.length < 2) return null;

      // 解析表头（第一行）
      const headers = dataLines[0].split('|').filter(cell => cell.trim() !== '').map(cell => cell.trim());

      // 解析表格数据（跳过分隔行）
      const rows = [];
      for (let i = 2; i < dataLines.length; i++) {
        const cells = dataLines[i].split('|').filter(cell => cell.trim() !== '').map(cell => cell.trim());
        if (cells.length > 0) {
          rows.push(cells);
        }
      }

      // 构建 HTML 表格
      let tableHtml = '<div class="markdown-table-wrapper"><table class="markdown-table">';
      tableHtml += '<thead class="markdown-thead"><tr class="markdown-tr">';
      headers.forEach(header => {
        tableHtml += `<th class="markdown-th">${header}</th>`;
      });
      tableHtml += '</tr></thead>';
      tableHtml += '<tbody class="markdown-tbody">';
      rows.forEach(row => {
        tableHtml += '<tr class="markdown-tr">';
        row.forEach(cell => {
          // 处理单元格中的简单 markdown
          let processedCell = cell
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*([^*]+)\*/g, '<em>$1</em>')
            .replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');
          tableHtml += `<td class="markdown-td">${processedCell}</td>`;
        });
        tableHtml += '</tr>';
      });
      tableHtml += '</tbody></table></div>';

      return tableHtml;
    };

    // 检测并处理表格 - 使用占位符标记表格位置
    const tablePattern = /^\|.*\|$\n^\|[-:\s|]+\|$\n(?:^\|.*\|$\n?)+/gm;
    const tables = [];
    let processedContent = content.replace(tablePattern, (match) => {
      const tableHtml = processTable(match);
      const placeholder = `__TABLE_${tables.length}__`;
      tables.push(tableHtml || match);
      return placeholder;
    });

    // 预处理内容 - 应用少数派风格指南规范
    processedContent = formatSspaiStyle(processedContent);

    processedContent = processedContent
      // 处理内联代码
      .replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>')
      // 处理代码块 - 转换为简单的 HTML
      .replace(/```(\w*)\n([\s\S]*?)```/g, (match, language, codeContent) => {
        const languageClass = language ? ` class="language-${language}"` : '';
        return `<pre class="markdown-pre"><code${languageClass}>${codeContent}</code></pre>`;
      })
      // 处理粗体
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // 处理斜体
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // 处理链接
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      // 处理标题
      .replace(/^### (.*$)/gm, '<h3 class="markdown-h3">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="markdown-h2">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 class="markdown-h1">$1</h1>')
      // 处理列表
      .replace(/^\* (.*$)/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>\n?)+/g, '<ul class="markdown-list">$&</ul>')
      // 处理引用
      .replace(/^> (.*$)/gm, '<blockquote>$1</blockquote>')
      // 处理换行
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br />');

    // 还原表格
    tables.forEach((tableHtml, index) => {
      processedContent = processedContent.replace(`__TABLE_${index}__`, tableHtml);
    });

    // 不要把所有内容包裹在 <p> 中，因为表格不能在 <p> 内部
    // 如果包含表格，直接返回内容；否则包裹在 <p> 中
    const hasTables = tables.length > 0;
    const htmlContent = hasTables ? processedContent : `<p>${processedContent}</p>`;

    return (
      <div
        className="markdown-renderer fallback-renderer"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  } catch (error) {
    console.error('SimpleMarkdownFallback error:', error);
    return <div className="markdown-renderer fallback-renderer"><p>{content}</p></div>;
  }
};

// 安全地预处理内容，处理特殊字符
const preprocessContent = (content) => {
  if (!content) return '';

  try {
    // 1. 应用少数派风格指南规范（中英文混排、标点符号等）
    let styledContent = formatSspaiStyle(content);

    // 2. 处理特殊字符，确保代码块正确解析
    return styledContent;
  } catch (error) {
    console.error('Content preprocessing error:', error);
    return content;
  }
};

// 检查内容是否包含可能导致react-markdown错误的模式
const hasProblematicMarkdownPatterns = (content) => {
  if (!content || typeof content !== 'string') return false;

  // 检查可能导致this.setData错误的模式
  const problematicPatterns = [
    // 表格 - 检测表格但会通过 try-catch 处理，而不是预先使用 fallback
    // 单独的代码块标记
    /```[^\s]*\n\s*```/,  // 空代码块
    /```\s*\n[^`]*\n```/,  // 可能有问题的小代码块
    // 特殊字符组合
    /```\s*\n\s*`/,       // 代码块开始后跟单个反引号
    /`\s*\n```/,          // 内联代码后跟代码块
    // 复杂嵌套结构
    /```\s*\n.*\n.*\n```/, // 多行代码块可能有问题
  ];

  return problematicPatterns.some(pattern => pattern.test(content));
};

// 检查内容是否包含表格
const hasTable = (content) => {
  if (!content || typeof content !== 'string') return false;
  return /^\|.*\|$/m.test(content) && /\|[-:\s|]+\|/.test(content);
};

const MarkdownRenderer = ({
  content,
  type = 'default',
  enableLineBreaks = false,
  skipHtml = true,
  className = '',
  customComponents = {},
  ...props
}) => {
  // React Hooks 必须在组件顶部按相同顺序调用
  const [useFallback, setUseFallback] = useState(false);
  const [errorKey, setErrorKey] = useState(0);

  // 错误处理函数
  const handleError = useCallback((error) => {
    // 检查是否是已知的 react-markdown 解析错误
    const isKnownMarkdownError = error.message && (
      error.message.includes('this.getData is not a function') ||
      error.message.includes('exitCodeText')
    );

    if (!isKnownMarkdownError) {
      // 对于未知错误，记录完整错误信息
      console.error('Unexpected ReactMarkdown error:', error);
    }

    setUseFallback(true);
    setErrorKey(prev => prev + 1); // 强制重新渲染
  }, []);

  if (!content) return null;

  // 预处理内容
  const processedContent = enableLineBreaks ? preprocessContent(content) : content;

  // 主动检查是否包含有问题的markdown模式
  const shouldUseFallbackImmediately = hasProblematicMarkdownPatterns(processedContent);

  // 根据类型确定类名
  const typeClassName = type ? `markdown-${type}` : '';
  const finalClassName = `markdown-renderer ${typeClassName} ${className}`.trim();

  // 错误边界组件
  class MarkdownErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
      return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
      // 检查是否是已知的 react-markdown 解析错误
      const isKnownMarkdownError = error.message && (
        error.message.includes('this.getData is not a function') ||
        error.message.includes('exitCodeText')
      );

      if (!isKnownMarkdownError) {
        // 只对未知错误记录详细信息
        console.error('MarkdownRenderer ErrorBoundary caught an unexpected error:', error, errorInfo);
      }

      // 调用父组件传递的错误处理函数
      if (this.props.onError) {
        this.props.onError(error);
      }
    }

    render() {
      if (this.state.hasError) {
        return null; // 返回null，让父组件处理fallback
      }

      return this.props.children;
    }
  }

  // 如果出现错误或检测到有问题的模式，使用fallback渲染器
  if (useFallback || shouldUseFallbackImmediately) {
    // 静默使用备用渲染器

    return (
      <div className={finalClassName} {...props}>
        <SimpleMarkdownFallback content={processedContent} />
      </div>
    );
  }

  // 调试模式：强制使用fallback来测试
  if (content && content.includes('```') && process.env.NODE_ENV === 'development') {
    // 临时启用fallback进行测试
    // return (
    //   <div className={finalClassName} {...props}>
    //     <SimpleMarkdownFallback content={processedContent} />
    //   </div>
    // );
  }

  // 自定义组件配置
  const components = {
    h1: ({ node, ...props }) => <h1 className="markdown-h1" {...props} />,
    h2: ({ node, ...props }) => <h2 className="markdown-h2" {...props} />,
    h3: ({ node, ...props }) => <h3 className="markdown-h3" {...props} />,
    h4: ({ node, ...props }) => <h4 className="markdown-h4" {...props} />,
    h5: ({ node, ...props }) => <h5 className="markdown-h5" {...props} />,
    h6: ({ node, ...props }) => <h6 className="markdown-h6" {...props} />,
    p: ({ node, ...props }) => <p className={`markdown-paragraph markdown-${type}-paragraph`} {...props} />,
    a: ({ node, ...props }) => <a target="_blank" rel="noopener noreferrer" {...props} />,
    img: ({ node, src, alt, title, ...props }) => (
      <img
        src={src}
        alt={alt || ''}
        title={title || alt || ''}
        className="responsive-image"
        loading="lazy"
        {...props}
      />
    ),
    pre: ({ node, ...props }) => <pre className="markdown-pre" {...props} />,
    code: ({ node, inline, className, children, ...props }) => {
      try {
        // 安全地处理 children，防止 undefined 或 null
        const safeChildren = children || '';
        const childrenString = typeof safeChildren === 'string' ? safeChildren : String(safeChildren);

        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : '';

        if (!inline && childrenString) {
          return (
            <code className={`language-${language} markdown-code-block`} {...props}>
              {childrenString}
            </code>
          );
        }

        return (
          <code className="inline-code" {...props}>
            {childrenString}
          </code>
        );
      } catch (error) {
        console.error('Code block rendering error:', error);
        handleError(error);
        // 返回安全的备用渲染
        return (
          <code className={inline ? 'inline-code' : 'markdown-code-block'} {...props}>
            {children || ''}
          </code>
        );
      }
    },
    blockquote: ({ node, ...props }) => <blockquote className="markdown-blockquote" {...props} />,
    ul: ({ node, ...props }) => <ul className={`markdown-list markdown-${type}-list`} {...props} />,
    ol: ({ node, ...props }) => <ol className={`markdown-ordered-list markdown-${type}-ordered-list`} {...props} />,
    li: ({ node, ...props }) => <li className={`markdown-list-item markdown-${type}-list-item`} {...props} />,
    table: ({ node, ...props }) => (
      <div className="markdown-table-wrapper">
        <table className="markdown-table" {...props} />
      </div>
    ),
    thead: ({ node, ...props }) => <thead className="markdown-thead" {...props} />,
    tbody: ({ node, ...props }) => <tbody className="markdown-tbody" {...props} />,
    tr: ({ node, ...props }) => <tr className="markdown-tr" {...props} />,
    th: ({ node, ...props }) => <th className="markdown-th" {...props} />,
    td: ({ node, ...props }) => <td className="markdown-td" {...props} />,
    hr: ({ node, ...props }) => <hr className="markdown-hr" {...props} />,
    strong: ({ node, ...props }) => <strong className="markdown-bold" {...props} />,
    em: ({ node, ...props }) => <em className="markdown-italic" {...props} />,
    del: ({ node, ...props }) => <del className="markdown-strikethrough" {...props} />,
    br: ({ node, ...props }) => <br className={`markdown-br markdown-${type}-br`} {...props} />,
  };

  // 合并自定义组件
  const finalComponents = { ...components, ...customComponents };

  // 添加 remark-breaks 插件来正确处理换行
  const remarkPlugins = enableLineBreaks ? [remarkGfm, remarkBreaks] : [remarkGfm];
  const rehypePlugins = [];

  // 安全的ReactMarkdown渲染器，带有try-catch
  const SafeReactMarkdown = ({ children }) => {
    try {
      // 检查是否包含表格 - 如果有表格，使用自定义的 fallback 表格渲染
      // 因为 remark-gfm 对某些表格会抛出 "this.setData is not a function" 错误
      const containsTable = hasTable(children);

      if (containsTable) {
        return <SimpleMarkdownFallback content={children} />;
      }

      return (
        <ReactMarkdown
          key={`markdown-${errorKey}`} // 使用key来强制重新渲染
          remarkPlugins={remarkPlugins}
          rehypePlugins={rehypePlugins}
          skipHtml={skipHtml}
          components={finalComponents}
          urlTransform={url => url}
        >
          {children}
        </ReactMarkdown>
      );
    } catch (error) {
      console.error('SafeReactMarkdown caught error:', error);
      handleError(error);
      return <SimpleMarkdownFallback content={children} />;
    }
  };

  return (
    <div className={finalClassName} {...props}>
      <MarkdownErrorBoundary onError={handleError}>
        <SafeReactMarkdown>
          {processedContent}
        </SafeReactMarkdown>
      </MarkdownErrorBoundary>
    </div>
  );
};

export default MarkdownRenderer;